import { moneyColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { InvoiceView } from "src/api/generated/erp/db/types/tables/invoiceView.ts";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { AccountingDimension } from "src/api/generated/erp/db/types/tables/accountingDimension.ts";
import {
	InvoiceLinesDataGridApi,
	InvoiceLinesDataGridApi_InvoiceLineDto,
} from "src/api/generated/erp/sales/invoicing/api/invoiceLinesDataGridApi.ts";
import { AavoGridColDef } from "src/components/common/dataGrid/types.tsx";
import { InvoiceLineForm } from "src/components/views/erp/sales/invoicing/InvoiceLineForm.tsx";
import { formatLabelWithCurrencyCode, renderMoneyCellWithCurrency } from "src/components/views/erp/sales/salesUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface InvoiceLinesDataGridProps {
	invoice: InvoiceView;
}

export const InvoiceLinesDataGrid = ({ invoice }: InvoiceLinesDataGridProps) => {
	return (
		<ClientSideDataGridModel
			customFunctionUiActionView={"INVOICE_LINES"}
			fetchData={() => InvoiceLinesDataGridApi.getDataGridData({ invoiceId: invoice.invoiceId })}
			getRows={(data) => data.invoiceLines}
			getRowId={(row) => row.invoiceLineId}
			initialParams={{}}
			gridId={"C128689FC7168A61"}
			render={({ dataGridProps, data: { accountingDimensions } }) => (
				<FormCrudDataGrid<InvoiceLinesDataGridApi_InvoiceLineDto>
					columns={[
						textColumn({
							field: "salesPartNo",
							headerName: i18n.t("part_no"),
							width: 150,
						}),
						textColumn({
							field: "linePartDescription",
							headerName: i18n.t("sales_part"),
							width: 150,
						}),
						textColumn({
							field: "mark",
							headerName: i18n.t("mark"),
						}),
						textColumn({
							field: "quantityInSalesUnits",
							headerName: i18n.t("quantity_in_sales_units"),
							width: 180,
						}),
						textColumn({
							field: "salesUnit",
							headerName: i18n.t("sales_unit"),
						}),
						textColumn({
							field: "quantityInPriceUnits",
							headerName: i18n.t("quantity_in_price_units"),
							width: 180,
						}),
						textColumn({
							field: "priceUnit",
							headerName: i18n.t("price_unit"),
						}),
						moneyColumn({
							field: "unitPrice",
							headerName: formatLabelWithCurrencyCode(
								i18n.t("unit_price_in_price_units"),
								invoice.currencyCode,
							),
							width: 200,
							renderCell: renderMoneyCellWithCurrency(invoice.currencyCode),
						}),
						textColumn({
							field: "discountPercent",
							headerName: i18n.t("discount_percent"),
						}),
						moneyColumn({
							field: "totalPrice",
							headerName: formatLabelWithCurrencyCode(i18n.t("sum_without_vat"), invoice.currencyCode),
							width: 150,
							renderCell: renderMoneyCellWithCurrency(invoice.currencyCode),
						}),
						moneyColumn({
							field: "totalVat",
							headerName: formatLabelWithCurrencyCode(i18n.t("vat"), invoice.currencyCode),
							renderCell: renderMoneyCellWithCurrency(invoice.currencyCode),
						}),
						moneyColumn({
							field: "totalPriceWithVat",
							headerName: formatLabelWithCurrencyCode(i18n.t("sum_with_vat"), invoice.currencyCode),
							width: 150,
							renderCell: renderMoneyCellWithCurrency(invoice.currencyCode),
						}),
						...getAccountingDimensionColumns(accountingDimensions),
					]}
					form={{
						editEnabled: true,
						addRowEnabled: false,
						dialogSize: "sm",
						dialogTitle: i18n.t("invoice_line"),
						agentContext: (row) => ({
							key: defineAgentUiContextKey("erp.sales.invoiceLine.formDialog"),
							pathSegments:
								row?.invoiceLineId ?
									[`${i18n.t("invoice_line")} ${row.invoiceLineId}`]
								:	[i18n.t("invoice_line")],
							data: {
								invoiceId: row?.invoiceId ?? invoice.invoiceId,
								invoiceLineId: row?.invoiceLineId ?? null,
								customerOrderLineId: row?.customerOrderLineId ?? null,
								salesPartId: row?.salesPartId ?? null,
							},
						}),
						component: ({ row, onCompleted, onFormEdited }) => {
							if (row == null) return null;
							return (
								<InvoiceLineForm
									invoiceLineId={row.invoiceLineId}
									onCompleted={onCompleted}
									onFormEdited={onFormEdited}
								/>
							);
						},
					}}
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.invoiceLines.grid"),
						data: { invoiceId: invoice.invoiceId },
						rowFields: ["invoiceLineId", "invoiceId", "customerOrderLineId", "salesPartId", "mark"],
						selectedRowPathSegment: (row) => `${i18n.t("invoice_line")} ${row.invoiceLineId}`,
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};

function getAccountingDimensionColumns(
	dimensions: AccountingDimension[],
): AavoGridColDef<InvoiceLinesDataGridApi_InvoiceLineDto>[] {
	return dimensions.map((dimension) =>
		textColumn({
			field: `accounting_dimension_${dimension.accountingDimensionId}` as any,
			headerName: dimension.type === "ACCOUNTS" ? i18n.t("account") : dimension.name,
			width: 100,
			valueGetter: (_, row) => {
				const accountingCode = row.accountingCodes.find(
					(ac) => ac.accountingDimensionId === dimension.accountingDimensionId,
				);
				return accountingCode?.accountingCodeCode ?? "";
			},
		}),
	);
}
